import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  HStack,
  Input,
  InputGroup,
  InputRightElement,
  Progress,
  Step,
  StepIcon,
  StepIndicator,
  StepNumber,
  StepSeparator,
  StepStatus,
  Stepper,
  Text,
  VStack,
} from '@chakra-ui/react';

const RegistrationForm = ({ onSubmit }) => {
  // Step state
  const [activeStep, setActiveStep] = useState(0);
  const totalSteps = 3;
  
  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    verificationCode: ''
  });
  
  // Error state
  const [errors, setErrors] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    verificationCode: '',
    form: ''
  });
  
  // UI state
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [resendDisabled, setResendDisabled] = useState(false);
  const [countdown, setCountdown] = useState(0);
  
  // Color mode values
  
  
  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };
  
  // Validate email format
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isValid = emailRegex.test(email);
    console.log(`Email validation for |${email}|: ${isValid} (Regex: ${emailRegex.toString()})`);
    return isValid;
  };
  
  // Countdown timer for code resend
  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    } else if (countdown === 0 && resendDisabled) {
      setResendDisabled(false);
    }
    return () => clearTimeout(timer);
  }, [countdown, resendDisabled]);

  // Handle next step
  const handleNextStep = () => {
    console.log('handleNextStep called, current step:', activeStep);
    console.log('Current form data:', formData);
    console.log('Current errors:', errors);
    
    // Validate current step before proceeding
    if (activeStep === 0) {
      console.log('Validating basic info...');
      const isValid = validateBasicInfo();
      console.log('Basic info validation result:', isValid);
      if (!isValid) {
        console.log('Basic info validation failed, not proceeding');
        return;
      }
      console.log('Basic info validation passed, proceeding to next step');
    }
    
    if (activeStep === 1) {
      console.log('Validating password...');
      const isValid = validatePassword();
      console.log('Password validation result:', isValid);
      if (!isValid) {
        console.log('Password validation failed, not proceeding');
        return;
      }
      console.log('Password validation passed, proceeding to send verification code');
    }
    
    // If going from step 1 to step 2, send verification code
    if (activeStep === 1) {
      console.log('Proceeding to send verification code');
      sendVerificationCode();
      // The sendVerificationCode function will set activeStep when successful
    } else {
      console.log('Moving to next step:', activeStep + 1);
      setActiveStep(prev => prev + 1);
    }
  };

  // Handle previous step
  const handlePrevStep = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  // Validate basic info (step 1)
  const validateBasicInfo = () => {
    let isValid = true;
    const newErrors = { ...errors };
    
    // Clear form error first
    newErrors.form = '';
    
    console.log('Validating first name:', formData.firstName);
    // Validate first name
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
      isValid = false;
      console.log('First name validation failed: empty');
    } else if (formData.firstName.trim().length < 2) {
      newErrors.firstName = 'First name must be at least 2 characters';
      isValid = false;
      console.log('First name validation failed: too short');
    } else {
      newErrors.firstName = '';
      console.log('First name validation passed');
    }
    
    console.log('Validating last name:', formData.lastName);
    // Validate last name
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
      isValid = false;
      console.log('Last name validation failed: empty');
    } else if (formData.lastName.trim().length < 2) {
      newErrors.lastName = 'Last name must be at least 2 characters';
      isValid = false;
      console.log('Last name validation failed: too short');
    } else {
      newErrors.lastName = '';
      console.log('Last name validation passed');
    }
    
    console.log('Validating email:', formData.email);
    // Validate email
    if (!formData.email) {
      newErrors.email = 'Email is required';
      isValid = false;
      console.log('Email validation failed: empty');
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
      isValid = false;
      console.log('Email validation failed: invalid format');
    } else {
      newErrors.email = '';
      console.log('Email validation passed');
    }
    
    console.log('Basic info validation final result:', { isValid, errors: newErrors });
    setErrors(newErrors);
    return isValid;
  };

  // Validate password (step 2)
  const validatePassword = () => {
    let isValid = true;
    const newErrors = { ...errors };
    
    // Validate password
    if (!formData.password) {
      newErrors.password = 'Password is required';
      isValid = false;
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
      isValid = false;
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      newErrors.password = 'Password must contain at least one uppercase letter, one lowercase letter, and one number';
      isValid = false;
    } else {
      newErrors.password = '';
    }
    
    // Validate confirm password
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
      isValid = false;
    } else if (formData.confirmPassword !== formData.password) {
      newErrors.confirmPassword = 'Passwords do not match';
      isValid = false;
    } else {
      newErrors.confirmPassword = '';
    }
    
    setErrors(newErrors);
    return isValid;
  };

  // Validate verification code (step 3)
  const validateVerificationCode = () => {
    let isValid = true;
    const newErrors = { ...errors };
    
    // Validate verification code
    if (!formData.verificationCode || formData.verificationCode.length !== 6) {
      newErrors.verificationCode = 'Please enter the 6-digit verification code';
      isValid = false;
    } else {
      newErrors.verificationCode = '';
    }
    
    setErrors(newErrors);
    return isValid;
  };

  // Send verification code
  const sendVerificationCode = async () => {
    setIsLoading(true);
    setErrors(prev => ({ ...prev, form: '' })); // Clear previous errors
    
    console.log('Starting registration process with:', { 
      email: formData.email, 
      name: `${formData.firstName} ${formData.lastName}`,
      passwordLength: formData.password.length,
      validEmail: validateEmail(formData.email)
    });
    
    // Double-check email validation before proceeding
    if (!validateEmail(formData.email)) {
      console.error('Email validation failed before registration attempt');
      setErrors(prev => ({
        ...prev,
        form: 'The email address is not valid.'
      }));
      setIsLoading(false);
    return;
  }
  
  try {
    // Register the user with Firebase
    // This will automatically send a verification email to the user
    console.log(`[RegistrationForm.js] Calling register function with: email:|${formData.email}|, password length:${formData.password ? formData.password.length : 'undefined'}, name:|${formData.firstName} ${formData.lastName}|`);
    
    const result = await register(
      formData.email,
      formData.password,
      `${formData.firstName} ${formData.lastName}`
    );
    console.log('Register function returned:', result);
    
    if (result && result.success) {
      console.log('Registration successful!', result.user);
      // Disable resend button for 60 seconds
      setResendDisabled(true);
      setCountdown(60);
      // Move to next step
      console.log('Moving to verification step');
      setActiveStep(prev => prev + 1);
    } else {
      console.error('Registration failed:', result);
      // Display the specific error from Firebase
      setErrors(prev => ({
        ...prev,
        form: (result && result.error) || 'An error occurred during registration. Please try again.'
      }));
    }
    } catch (error) {
      console.error('Error sending verification email:', error);
      setErrors(prev => ({
        ...prev,
        form: `Failed to send verification email: ${error.message || 'Unknown error'}`
      }));
    } finally {
      setIsLoading(false);
    }
  };

  // Resend verification code
  const resendVerificationCode = async () => {
    if (resendDisabled) return;
    
    setIsLoading(true);
    setResendDisabled(true);
    setCountdown(60);
    
    try {
      console.log('Attempting to resend verification email to:', formData.email);
      const result = await register(
        formData.email,
        formData.password,
        `${formData.firstName} ${formData.lastName}`
      );
      
      if (result && result.success) {
        console.log('Verification email resent successfully');
      } else {
        console.log('User likely exists already, this is expected');
      }
      
      // Show success message regardless (we don't want to expose if user exists or not)
      console.log(`Verification email resent to ${formData.email}`);
    } catch (error) {
      // Expected error if user exists; do not show error to user
      console.log('Expected error during resend (user likely exists):', error.message);
    } finally {
      setIsLoading(false);
    }
  };


  // Get auth context
  const { register } = useAuth();
  const navigate = useNavigate();
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate verification code
    if (!validateVerificationCode()) {
      return;
    }
    
    try {
      setIsLoading(true);
      
      // In Firebase, email verification is typically done by clicking a link in the email
      // rather than entering a code. Here we're just using the verification code step as a UI gate
      // The actual verification happens when the user clicks the link in their email
      
      // Call the onSubmit prop with form data if provided
      if (onSubmit) {
        onSubmit(formData);
      }
      
      // Redirect to login page with success message
      navigate('/login', { 
        state: { 
          message: 'Registration successful! Please check your email to verify your account, then log in.' 
        } 
      });
    } catch (error) {
      console.error('Registration error:', error);
      setErrors(prev => ({
        ...prev,
        form: 'An error occurred during registration. Please try again.'
      }));
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <Box
      as="form"
      onSubmit={activeStep === totalSteps - 1 ? handleSubmit : (e) => e.preventDefault()}
      bg="bg.surface"
      p={8}
      borderWidth="1px"
      borderColor="border.primary"
      borderRadius="lg"
      boxShadow="lg"
      width="100%"
      maxW="500px"
    >
      <VStack spacing={6} align="flex-start" width="100%">
        <Text fontSize="2xl" fontWeight="bold" color="text.primary">Create Account</Text>
        
        {/* Stepper */}
        <Stepper index={activeStep} width="100%" colorScheme="teal" size="sm">
          {[...Array(totalSteps)].map((_, index) => (
            <Step key={index}>
              <StepIndicator>
                <StepStatus
                  complete={<StepIcon />}
                  incomplete={<StepNumber />}
                  active={<StepNumber />}
                />
              </StepIndicator>
              <StepSeparator />
            </Step>
          ))}
        </Stepper>
        
        {/* Step Title */}
        <Text fontWeight="medium" fontSize="lg" color="text.primary">
          {activeStep === 0 ? 'Basic Information' : 
           activeStep === 1 ? 'Create Password' : 
           'Check Your Email'}
        </Text>
        
        {/* Progress indicator */}
        <Progress
          value={(activeStep + 1) / totalSteps * 100}
          width="100%"
          colorScheme="teal"
          borderRadius="md"
          size="sm"
        />
        
        {errors.form && (
          <Box 
            p={3} 
            bg="error.bg" 
            color="error.textOnBg" 
            width="100%" 
            borderRadius="md"
          >
            {errors.form}
          </Box>
        )}
        
        {/* Step 1: Basic Information */}
        {activeStep === 0 && (
          <>
            {/* First Name Field */}
            <FormControl isInvalid={!!errors.firstName} isRequired>
              <FormLabel htmlFor="firstName" color="text.secondary">First Name</FormLabel>
              <Input
                id="firstName"
                name="firstName"
                type="text"
                value={formData.firstName}
                onChange={handleChange}
                placeholder="John"
                bg="input.bg"
                borderColor="input.border"
                color="input.text"
                _placeholder={{ color: 'input.placeholder' }}
              />
              <FormErrorMessage>{errors.firstName}</FormErrorMessage>
            </FormControl>
            
            {/* Last Name Field */}
            <FormControl isInvalid={!!errors.lastName} isRequired>
              <FormLabel htmlFor="lastName" color="text.secondary">Last Name</FormLabel>
              <Input
                id="lastName"
                name="lastName"
                type="text"
                value={formData.lastName}
                onChange={handleChange}
                placeholder="Doe"
                bg="input.bg"
                borderColor="input.border"
                color="input.text"
                _placeholder={{ color: 'input.placeholder' }}
              />
              <FormErrorMessage>{errors.lastName}</FormErrorMessage>
            </FormControl>
            
            {/* Email Field */}
            <FormControl isInvalid={!!errors.email} isRequired>
              <FormLabel htmlFor="email" color="text.secondary">Email</FormLabel>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="your.email@example.com"
                bg="input.bg"
                borderColor="input.border"
                color="input.text"
                _placeholder={{ color: 'input.placeholder' }}
              />
              <FormErrorMessage>{errors.email}</FormErrorMessage>
            </FormControl>
          </>
        )}
        
        {/* Step 2: Password Creation */}
        {activeStep === 1 && (
          <>
            {/* Password Field */}
            <FormControl isInvalid={!!errors.password} isRequired>
              <FormLabel htmlFor="password" color="text.secondary">Password</FormLabel>
              <InputGroup>
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Create a strong password"
                  bg="input.bg"
                  borderColor="input.border"
                  color="input.text"
                  _placeholder={{ color: 'input.placeholder' }}
                />
                <InputRightElement width="4.5rem">
                  <Button
                    h="1.75rem"
                    size="sm"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? 'Hide' : 'Show'}
                  </Button>
                </InputRightElement>
              </InputGroup>
              <FormErrorMessage>{errors.password}</FormErrorMessage>
            </FormControl>
            
            {/* Confirm Password Field */}
            <FormControl isInvalid={!!errors.confirmPassword} isRequired>
              <FormLabel htmlFor="confirmPassword" color="text.secondary">Confirm Password</FormLabel>
              <InputGroup>
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Confirm your password"
                  bg="input.bg"
                  borderColor="input.border"
                  color="input.text"
                  _placeholder={{ color: 'input.placeholder' }}
                />
                <InputRightElement width="4.5rem">
                  <Button
                    h="1.75rem"
                    size="sm"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? 'Hide' : 'Show'}
                  </Button>
                </InputRightElement>
              </InputGroup>
              <FormErrorMessage>{errors.confirmPassword}</FormErrorMessage>
            </FormControl>
          </>
        )}
        
        {/* Step 3: Email Verification Info */}
        {activeStep === 2 && (
          <>
            <Text>
              A verification link has been sent to <strong>{formData.email}</strong>. 
              Please check your inbox (and spam folder) and click the link to verify your email address.
            </Text>
            <Text mt={4}>
              Once verified, you can proceed to log in.
            </Text>
            <Button 
              onClick={resendVerificationCode} // This function might need adjustment if it assumes a code was sent
              isLoading={isLoading}
              isDisabled={resendDisabled}
              variant="link"
              color="brand.primary"
              mt={4}
            >
              Resend Verification Email {resendDisabled ? `(${countdown}s)` : ''}
            </Button>
          </>
        )}
        
        {/* Navigation Buttons */}
        {activeStep < totalSteps - 1 && (
          <HStack width="100%" justify="space-between" mt={4}>
          {activeStep > 0 ? (
            <Button
              variant="outline"
              onClick={handlePrevStep}
              isDisabled={isLoading}
            >
              Back
            </Button>
          ) : (
            <Box></Box> // Empty box for spacing
          )}
          
          {activeStep < totalSteps - 1 ? (
            <Button
              id="next-step-button"
              bg="button.primary.bg" color="button.primary.text" _hover={{ bg: 'brand.primary' }}
              onClick={() => {
                console.log('Next button clicked, calling handleNextStep');
                handleNextStep();
              }}
              isLoading={isLoading && activeStep === 1}
              loadingText="Sending Code"
            >
              Next
            </Button>
          ) : (
            <Button
              type="submit"
              bg="button.primary.bg" color="button.primary.text" _hover={{ bg: 'brand.primary' }}
              isLoading={isLoading}
              loadingText="Creating Account"
            >
              Complete Registration
            </Button>
          )}
        </HStack>
        )}
        
        <Box textAlign="center" width="100%" mt={2}>
          <Text fontSize="sm">
            Already have an account?{' '}
            <Button variant="link" color="link.primary" size="sm" as="a" href="/login">
              Sign in instead
            </Button>
          </Text>
        </Box>
      </VStack>
    </Box>
  );
};

export default RegistrationForm;
